// src/App.tsx
import React, { StrictMode } from "react";
import {
  Route,
  Routes,
  Navigate,
  useLocation,
  HashRouter,
} from "react-router-dom";
import { Box, CssBaseline, ThemeProvider, useMediaQuery, useTheme } from "@mui/material";
import { theme } from "./styles/theme";

import Header from "./components/Header";
import Footer from "./components/Footer";
import MainPage from "./pages/MainPage";
import CoursesPage from "./pages/CoursesPage";
import RegisterPage from "./pages/RegisterPage";
import ResourcesPage from "./pages/ResourcesPage";
import ContactPage from "./pages/ContactPage";
import CourseManagementPage from "./pages/Administration/CourseManagementPage";
import UserManagementPage from "./pages/Administration/UserManagementPage";
import ReportsPage from "./pages/Administration/ReportsPage";
import ManageArchivedCourses from "./pages/Administration/ManageArchivedCourses";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import "./fontawesome";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ReactDOM from "react-dom";
import PasswordSetResetPage from "./pages/PasswordSetResetPage";
import LoginPage from "./pages/LoginPage";
import OnlineCoursePage from "./pages/OnlineCoursePage";
import {
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import ParticipantsPage from "./pages/Administration/ParticipantsPage";
import MobileHeader from "./components/MobileHeader";
import { LoginDialog } from "./components/LoginDialog";
import { useLoginDialog } from "./hooks/useLoginDialog";

const ProtectedRoute: React.FC<{ element: React.ReactElement }> = ({
  element,
}) => {
  const { isLoggedIn, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return isLoggedIn ? (
    element
  ) : (
    <Navigate
      to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
      replace
    />
  );
};

const AppContent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const loginDialog = useLoginDialog();

  
  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <Box sx={{ position: "sticky", top: 0, zIndex: 1100 }}>
        {isMobile ? <MobileHeader /> : <Header />}
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", // Add this to prevent horizontal scrolling
        }}
      >
        <Routes>
          <Route path="/" element={<MainPage />} />
          <Route path="/courses" element={<CoursesPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/online-course" element={<OnlineCoursePage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/users/reset-password"
            element={<PasswordSetResetPage />}
          />
          <Route
            path="/users/set-initial-password"
            element={<PasswordSetResetPage />}
          />
          <Route
            path="/admin/courses"
            element={<ProtectedRoute element={<CourseManagementPage />} />}
          />
          <Route
            path="/admin/archived-courses"
            element={<ProtectedRoute element={<ManageArchivedCourses />} />}
          />
          <Route
            path="/admin/participants"
            element={<ProtectedRoute element={<ParticipantsPage />} />}
          />
          <Route
            path="/admin/users"
            element={<ProtectedRoute element={<UserManagementPage />} />}
          />
          <Route
            path="/admin/reports"
            element={<ProtectedRoute element={<ReportsPage />} />}
          />
        </Routes>
      </Box>
      <Footer onLoginClick={() => loginDialog.setIsOpen(true)} />
      <LoginDialog
        isOpen={loginDialog.isOpen}
        setIsOpen={loginDialog.setIsOpen}
      />
    </Box>
  );
};

const ToastPortal: React.FC = () => {
  return ReactDOM.createPortal(
    <ToastContainer
      position="top-center"
      autoClose={5000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="colored"
    />,
    document.body
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

// Create a persister
const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

// Set up persistence
persistQueryClient({
  queryClient,
  persister,
  maxAge: 1000 * 60 * 60 * 24, // 24 hours
});

const showDevTools =
  process.env.NODE_ENV === "development" ||
  localStorage.getItem("showDevTools") === "true";

const App: React.FC = () => {
  return (
    <StrictMode>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <HashRouter basename="/">
            <QueryClientProvider client={queryClient}>
              <AppContent />
              {showDevTools && <ReactQueryDevtools initialIsOpen={false} />}
            </QueryClientProvider>
            <ToastPortal />
          </HashRouter>
        </AuthProvider>
      </ThemeProvider>
    </StrictMode>
  );
};

export default App;
